/**
 * userEngineConfig.tsx — config silnika formatek dla kartoteki Użytkownik
 * (keystone unifikacja-formatek — mirror `contractorEngineConfig.tsx` / `companyEngineConfig.tsx`).
 *
 * WSZYSTKIE pola core użytkownika dostają custom renderer — zero natywnej delegacji do
 * `ExtFieldValueInput` (mirror kontrahenta/firmy), więc gałąź natywna silnika jest martwa dla
 * tej kartoteki — `labelSource`/`coerceEmptyToNull` zostają kosmetyczne.
 *
 * Etykiety: `ctx.field.name` (definicja pola z `GET /system-modules/users/fields`, DB-driven),
 * NIE i18n — zachowanie 1:1 z `userCoreRenderers.tsx` (przed migracją, plik usunięty razem
 * z rejestrem `USER_CORE_RENDERERS`, silnik go zastępuje).
 *
 * Quirki portowane 1:1:
 *  - `email`: readonly w edit, edytowalny CoreInput w create (`ctx.isCreate`).
 *  - `username`: edytowalny TYLKO create+human — create+non-human zwraca `null` (POST /users
 *    (api/service) wylicza login serwerowo po stronie backendu, endpoint go nie przyjmuje).
 *  - `user_type`: edytowalny Dropdown w create, readonly w edit.
 *  - `is_super_user`: renderuje się TYLKO gdy `true` (informacyjne — status super-usera jest
 *    plakietką w nagłówku, nadawanie nie idzie z tego ekranu).
 *  - `default_company_id`: ukryty (`null`) w create (wybór idzie przez zakładkę „Firmy" spośród
 *    faktycznie przypisanych) — w edit Dropdown zasilany listą firm z `ctx.extra.companies`.
 *  - `groups`/`category_id`: jak w Kontrahentach/Firmach (GroupsInput / RecordCategoryPicker).
 *  - `employee_id` („Pracownik"): ukryty (`null`) w create (powiązanie z pracownikiem ustawia się
 *    dopiero na zapisanym koncie — kandydaci zależą od firm, do których konto ma dostęp) oraz dla
 *    kont innych niż „człowiek" (serwer odrzuca powiązanie: USER_TYPE_CANNOT_LINK_EMPLOYEE); w edit
 *    wyszukiwalny wybór kandydatów (`EmployeeCandidateSelect`, serwerowy search), a bez trybu edycji
 *    (`ctx.editable=false`) — wartość tylko do odczytu „Imię Nazwisko — Firma". Pod polem ostrzeżenie,
 *    gdy konto nie ma dostępu do firmy pracownika.
 *  - `status`/`last_login_at`/`invited_at`: zawsze readonly (zarządzane przez inne endpointy/
 *    zadania cron), formatowanie znacznika czasu przez `ctx.form.language`.
 *
 * Kanał `extra` (UserExtra) niesie `companies` (lista firm tenanta dla Dropdownu
 * `default_company_id`) oraz `userId` + `employee` (kontekst pola „Pracownik"). Wcześniej
 * `useCompanies()` żyło w `UserDataTab`, teraz (jak kanał NIP kontrahenta / `initialPrimary` firmy)
 * — w `UserDetail.tsx`, wpływa do silnika przez `runtime.extra` (silnik wymaga JEDNEGO kierunku
 * przepływu: strona → runtime → renderer).
 */

import { useTranslation } from 'react-i18next';
import { RECORD_LANGUAGES } from '@saas/shared';

import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { InlineMessage } from '@/components/InlineMessage';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderContext,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { recordLanguageLabel } from '@/lib/core-field-option-labels';
import type { UserDetail, UserEmployeeSummary } from '@/lib/use-tenant-people';

import {
  EmployeeCandidateSelect,
  employeeOptionLabel,
  type PickedEmployee,
} from './EmployeeCandidateSelect';
import { USER_CORE_FIELD_SLUGS, type UserFormData } from './UserDataTab';

/** Opcja pickera firmy — `id` + etykieta wyliczona (shortName||legalName), buduje `UserDetail.tsx`. */
export interface UserLookupOption {
  id: string;
  label: string;
}

/**
 * Stan runtime specyficzny dla użytkownika, niesiony przez `FieldRenderContext.extra` /
 * `KartotekaRenderRuntime.extra` (silnik go nie interpretuje — patrz engine-config.ts).
 * `companies` — lista firm tenanta dla renderera `default_company_id` (`UserDetail.tsx` woła
 * `useCompanies()`, przeniesione z `UserDataTab`, i mapuje na `{ id, label }`).
 * `userId` + `userType` + `employee` — kontekst renderera `employee_id`: id edytowanego konta
 * (zapytanie o kandydatów), jego typ (pole tylko dla `human`) i podsumowanie zapisanego
 * pracownika z GET rekordu (etykieta w trybie odczytu, ostrzeżenie o braku dostępu do jego firmy).
 * W create wszystkie trzy to `null`.
 * `pickedEmployee` + `onEmployeePicked` — etykieta pracownika wybranego z listy, trzymana przez
 * stronę: po zapisie rekord odświeża się chwilę później, a tryb odczytu nie może w tym oknie
 * pokazać „—" zamiast właśnie zapisanego pracownika.
 */
export interface UserExtra {
  companies: UserLookupOption[];
  userId: string | null;
  userType: UserDetail['userType'] | null;
  employee: UserEmployeeSummary | null;
  pickedEmployee: PickedEmployee | null;
  onEmployeePicked: (picked: PickedEmployee) => void;
}

type Ctx = FieldRenderContext<UserFormData, UserExtra>;

/**
 * Ramka błędu dla `Dropdown` (który nie ma własnego propa `invalid`) — ten sam wizualny
 * język co `INVALID_CLASS` w `CoreInput.tsx`. Podawana przez `className` (scalane przez
 * tailwind-merge — ostatnia klasa wygrywa konflikt border-*).
 */
const DROPDOWN_INVALID_CLASS = 'border-red-500 ring-1 ring-red-500';

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput {...props} />;
}

function ReadonlyValue({ value, testId }: { value: string | null; testId?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="w-full rounded-lg border border-transparent bg-bg-secondary px-3 py-1.5 text-sm text-text-tertiary"
      data-testid={testId}
    >
      {value ?? t('common.notSet')}
    </div>
  );
}

function LanguageSelect({ ctx }: { ctx: Ctx }) {
  return (
    <Dropdown
      value={ctx.form.language}
      onChange={(v) => ctx.setField({ language: v as UserFormData['language'] })}
      disabled={!ctx.editable}
      data-testid="user-field-language"
      className={ctx.hasError?.('language') ? DROPDOWN_INVALID_CLASS : undefined}
      options={RECORD_LANGUAGES.map((code) => ({
        value: code,
        label: recordLanguageLabel(code, ctx.t),
      }))}
    />
  );
}

const CREATE_USER_TYPE_OPTIONS = ['human', 'api', 'service'] as const;

function UserTypeSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Dropdown
      value={ctx.form.userType}
      onChange={(v) => ctx.setField({ userType: v as UserFormData['userType'] })}
      data-testid="user-field-userType"
      options={CREATE_USER_TYPE_OPTIONS.map((k) => ({
        value: k,
        label: t(`tenant.users.detail.create.kind.${k}`),
      }))}
    />
  );
}

function DefaultCompanySelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const options = ctx.extra?.companies ?? [];
  const dropdownOptions: DropdownOption[] = [
    { value: '', label: t('common.notSet') },
    ...options.map((o) => ({ value: o.id, label: o.label })),
  ];
  return (
    <Dropdown
      value={ctx.form.defaultCompanyId ?? ''}
      onChange={(v) => ctx.setField({ defaultCompanyId: v || null })}
      disabled={!ctx.editable}
      data-testid="user-field-defaultCompanyId"
      className={ctx.hasError?.('defaultCompanyId') ? DROPDOWN_INVALID_CLASS : undefined}
      options={dropdownOptions}
    />
  );
}

function formatTs(iso: string | null, lang: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(lang);
}

// ─── Ground-truth fieldType per slug (z seeda, apps/api/lib/seed-system-modules.ts
// → users.coreFields) — używany WYŁĄCZNIE przez fallback statyczny silnika (403 na
// metadanych modułu). Renderer i tak owns widget. ───────────────────────────────
const USER_FIELD_TYPES: Record<string, string> = {
  groups: 'multiselect',
  category_id: 'select',
  email: 'email',
  username: 'varchar',
  display_name: 'varchar',
  language: 'select',
  user_type: 'select',
  is_super_user: 'boolean',
  default_company_id: 'reference',
  status: 'select',
  last_login_at: 'timestamp',
  invited_at: 'timestamp',
  employee_id: 'reference',
};

// ─── Mapa renderers (slug → FieldRenderer), portowane 1:1 z userCoreRenderers.tsx ──────

/**
 * Grupy ze słownika `user_groups` — wielokrotny wybór. Renderer TUTAJ, żeby o miejscu pola
 * na formatce decydował LAYOUT z buildera (wzorzec 1:1 z kontrahentami/pracownikami/firmami).
 */
const groupsRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <GroupsInput
      dictionaryCode="user_groups"
      value={ctx.form.groups}
      onChange={(next) => ctx.setField({ groups: next })}
      disabled={!ctx.editable}
      data-testid="user-groups"
    />
  </Field>
);

/**
 * Kategoria z realnej tabeli `record_categories` (kartoteka_key='users') — pojedynczy wybór,
 * DYSTYNKTYWNY od `groupsRenderer` (wiele, słownik). `RecordCategoryPicker` rysuje własny
 * `<Field>`, więc renderer nie owija go drugi raz. Migracja 0130 — patrz
 * `apps/api/lib/records/categories.ts`.
 */
const categoryRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <RecordCategoryPicker
    kartotekaKey="users"
    value={ctx.form.categoryId}
    onChange={(next) => ctx.setField({ categoryId: next })}
    editable={ctx.editable}
    testId="user-category"
  />
);

const emailRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.isCreate ? true : ctx.field.isRequired}
    error={ctx.getError?.('email')}
  >
    {ctx.isCreate ? (
      <TextInput
        type="email"
        value={ctx.form.email}
        onChange={(v) => ctx.setField({ email: v })}
        invalid={ctx.hasError?.('email')}
        testId="user-field-email"
      />
    ) : (
      <ReadonlyValue value={ctx.form.email} testId="user-field-email" />
    )}
  </Field>
);

/**
 * Create: edytowalny TYLKO dla "człowiek" — api/service dostają login wyliczony po stronie
 * serwera (`deriveUsernameFromEmail`), POST /users go nie przyjmuje.
 */
const usernameRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => {
  if (ctx.isCreate) {
    if (ctx.form.userType !== 'human') return null;
    return (
      <Field label={ctx.field.name} required error={ctx.getError?.('username')}>
        <TextInput
          value={ctx.form.username}
          onChange={(v) => ctx.setField({ username: v })}
          invalid={ctx.hasError?.('username')}
          testId="user-field-username"
        />
      </Field>
    );
  }
  return (
    <Field label={ctx.field.name} required={ctx.field.isRequired}>
      <ReadonlyValue value={ctx.form.username} testId="user-field-username" />
    </Field>
  );
};

const displayNameRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('displayName')}
  >
    <TextInput
      testId="user-field-displayName"
      value={ctx.form.displayName ?? ''}
      onChange={(v) => ctx.setField({ displayName: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('displayName')}
    />
  </Field>
);

const languageRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('language')}>
    <LanguageSelect ctx={ctx} />
  </Field>
);

const userTypeRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired}>
    {ctx.isCreate ? (
      <UserTypeSelect ctx={ctx} />
    ) : (
      <ReadonlyValue value={ctx.form.userType} testId="user-field-userType" />
    )}
  </Field>
);

/**
 * Pole informacyjne (read-only). Pokazujemy JE TYLKO dla faktycznych super-adminów —
 * dla zwykłego usera szary input „Super-user: —" tylko myli (sugeruje ustawialny status,
 * a super-usera i tak nie nadaje się z tego ekranu; status super jest plakietką w nagłówku).
 */
const isSuperUserRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) =>
  ctx.form.isSuperUser ? (
    <Field label={ctx.field.name}>
      <ReadonlyValue value="✓" testId="user-field-isSuperUser" />
    </Field>
  ) : null;

/**
 * W trybie CREATE domyślną firmę wybiera się w zakładce „Firmy" (spośród faktycznie
 * przypisanych) — tu ją chowamy, żeby nie dublować kontrolki i nie pokazywać wyboru spośród
 * wszystkich firm zanim user w ogóle przypisze jakąś.
 */
const defaultCompanyIdRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) =>
  ctx.isCreate ? null : (
    <Field
      label={ctx.field.name}
      required={ctx.field.isRequired}
      error={ctx.getError?.('defaultCompanyId')}
    >
      <DefaultCompanySelect ctx={ctx} />
    </Field>
  );

/**
 * Pracownik powiązany z kontem. Create → ukryty (kandydaci zależą od firm zapisanego konta); konta
 * inne niż „człowiek" → ukryty (serwer nie pozwala ich powiązać z pracownikiem).
 * Edit: wybór z wyszukiwarką (`ctx.editable`), inaczej wartość tylko do odczytu. Ostrzeżenie pod
 * polem, gdy zapisany pracownik jest z firmy, do której konto nie ma dostępu.
 */
const employeeIdRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => {
  if (ctx.isCreate) return null;
  if (ctx.extra?.userType !== 'human') return null;
  const userId = ctx.extra?.userId ?? null;
  const employee = ctx.extra?.employee ?? null;
  const pickedEmployee = ctx.extra?.pickedEmployee ?? null;
  // Podsumowanie dotyczy ZAPISANEGO powiązania — po zmianie wyboru (przed zapisem) nie opisuje
  // już bieżącej wartości formularza, więc ani etykieta odczytu, ani ostrzeżenie się do niej nie odnoszą.
  const saved = employee && employee.id === ctx.form.employeeId ? employee : null;
  // Tryb odczytu tuż po zapisie: formularz ma już nowego pracownika, a rekord z serwera jeszcze
  // starego (odświeża się w tle) — etykieta wybranej pozycji, żeby nie mignęło „—".
  const readonlyLabel = saved
    ? employeeOptionLabel(ctx.t, saved)
    : pickedEmployee && pickedEmployee.id === ctx.form.employeeId
      ? pickedEmployee.label
      : null;
  return (
    <Field
      label={ctx.field.name}
      required={ctx.field.isRequired}
      error={ctx.getError?.('employeeId')}
    >
      {ctx.editable && userId ? (
        <EmployeeCandidateSelect
          userId={userId}
          value={ctx.form.employeeId}
          onChange={(next) => ctx.setField({ employeeId: next })}
          currentEmployee={employee}
          pickedEmployee={pickedEmployee}
          onPick={ctx.extra?.onEmployeePicked}
        />
      ) : (
        <ReadonlyValue value={readonlyLabel} testId="user-field-employeeId" />
      )}
      {saved && !saved.companyAccessible && (
        <InlineMessage tone="warning" className="mt-1" data-testid="user-field-employeeId-warning">
          {saved.companyName
            ? ctx.t('tenant.users.detail.employee.companyNotAccessible', {
                company: saved.companyName,
              })
            : ctx.t('tenant.users.detail.employee.companyNotAccessibleUnknown')}
        </InlineMessage>
      )}
    </Field>
  );
};

const statusRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired}>
    <ReadonlyValue value={ctx.form.status} testId="user-field-status" />
  </Field>
);

const lastLoginAtRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired}>
    <ReadonlyValue
      value={formatTs(ctx.form.lastLoginAt, ctx.form.language)}
      testId="user-field-lastLoginAt"
    />
  </Field>
);

const invitedAtRenderer: FieldRenderer<UserFormData, UserExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired}>
    <ReadonlyValue
      value={formatTs(ctx.form.invitedAt, ctx.form.language)}
      testId="user-field-invitedAt"
    />
  </Field>
);

const USER_RENDERERS: Record<string, FieldRenderer<UserFormData, UserExtra>> = {
  groups: groupsRenderer,
  category_id: categoryRenderer,
  email: emailRenderer,
  username: usernameRenderer,
  display_name: displayNameRenderer,
  language: languageRenderer,
  user_type: userTypeRenderer,
  is_super_user: isSuperUserRenderer,
  default_company_id: defaultCompanyIdRenderer,
  status: statusRenderer,
  last_login_at: lastLoginAtRenderer,
  invited_at: invitedAtRenderer,
  employee_id: employeeIdRenderer,
};

const fields: Record<string, CoreFieldConfig<UserFormData, UserExtra>> = {};
for (const slug of Object.keys(USER_CORE_FIELD_SLUGS)) {
  const stateKey = USER_CORE_FIELD_SLUGS[slug]!;
  fields[slug] = {
    stateKey,
    fieldType: USER_FIELD_TYPES[slug] ?? 'varchar',
    renderer: USER_RENDERERS[slug]!,
    // Jedyne pole z colSpan=2 (rysuje go zresztą samo na swoim <Field> — wpis tu jest
    // kosmetyczny, patrz komentarz nagłówka pliku, gałąź natywna silnika jest martwa
    // dla tej kartoteki).
    ...(slug === 'groups' ? { colSpan: 2 as const } : {}),
  };
}

export const userEngineConfig: KartotekaEngineConfig<UserFormData, UserExtra> = {
  moduleSlug: 'users',
  // LOAD-BEARING: engine's ext delegation emits `${testIdPrefix}-${slug}` — testid
  // `user-ext-<slug>` (zachowanie 1:1 z przed-silnikowym UserDataTab).
  testIdPrefix: 'user-ext',
  coreFieldSlugs: Object.keys(USER_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
